import { useEffect, useState } from "react";
import { displayToast } from "../../../components/app/AppToast";
import { useLoginAuth } from "./useLoginAuth";
import { useTranslation } from "react-i18next";
import {
  gaEventAuthFailed,
  gaEventAuthMethodSelected,
  gaEventSignupSheetOpened,
  resolveAuthErrorCode,
} from "../../../tools/analytics/domains/onboarding.analytics";
import {
  lookupExistingProvider,
  loginWithEmailOtp,
} from "../../../services/firebase";
import { useSignupOnboardingState } from "../../auth/hooks/useSignupOnboardingState";
import { classifyEmailForAuth } from "../../auth/checkEmailOutcome";
import {
  readPendingEmailCode,
  useEmailOtp,
} from "../../auth/hooks/useEmailOtp";

/**
 * Hook d'orchestration pour LoginPage
 * Centralise toute la logique métier et les interactions entre hooks
 */
export function useLoginPage(initialEmail: string | null) {
  const { t } = useTranslation();
  // A login code mailed before the app was reloaded or killed under the reader,
  // who left it to read the mail. The page reopens on the six boxes for it.
  const [resumedEmail] = useState(() => {
    const pending = readPendingEmailCode();
    if (pending === null || pending.intent !== "login") return null;
    if (
      initialEmail !== null &&
      initialEmail.trim().toLowerCase() !== pending.email
    )
      return null;
    return pending.email;
  });
  const auth = useLoginAuth(
    resumedEmail === null ? initialEmail : resumedEmail
  );
  const { isInvitedToRoom } = useSignupOnboardingState();
  const otp = useEmailOtp({ intent: "login", surface: "login_page" });
  const [code, setCode] = useState("");

  useEffect(() => {
    if (resumedEmail === null) return;
    // Resume only: a record that expired since the initializer above leaves the
    // address filled in and mails nothing the reader did not ask for.
    otp.resumeCode(resumedEmail);
  }, []);

  // Page-view analytics only. Redirecting an already-authenticated visitor away
  // from /login is the router's job (AppRoutes' logged-in guard), not a mount
  // check inside the page.
  useEffect(() => {
    gaEventSignupSheetOpened({
      surface: "login_page",
      is_invited_to_room: isInvitedToRoom,
    });
  }, []);

  /**
   * Ask for a code. The page swaps to the code view on the hook's own state, so
   * a throttled request still lands there: the user was throttled because they
   * asked a moment ago, which means a code is already in their mailbox.
   *
   * Takes no event: the form owns `preventDefault`, and a hook that takes a DOM
   * event is a hook that can only be called from a form.
   */
  async function handleRequestCode() {
    gaEventAuthMethodSelected({
      method: "email",
      intent: "login",
      surface: "login_page",
    });

    // Ask who this address is BEFORE asking for a code. `otp/request` answers
    // identically for a known and an unknown address, because it must (the
    // screen would otherwise be an address oracle), and it mails NOTHING for a
    // login on an address with no account. Without this check the six boxes
    // open over a mailbox that will never receive anything: the silent lost
    // login the PRD's guardrail exists to catch.
    //
    // The other three doors already ask (`checkEmailOutcome.ts`). This one is
    // the fourth and was the only one that did not.
    const identified = await classifyEmailForAuth(auth.email);
    switch (identified.kind) {
      case "invalid":
        displayToast(t("Please input a valid email address."), "warning");
        return;
      case "failed":
        displayToast(t("Something went wrong. Please try again."), "error");
        return;
      case "new":
        // Not routed to signup on the operator's behalf: this door says "log
        // in", and turning a mistyped address into a new account is how a typo
        // becomes a duplicate. The Sign up link is on the same screen.
        displayToast(
          t("No Juisci account uses this address yet. Sign up to create one."),
          "warning"
        );
        return;
      case "existing":
        break;
      default:
        throw new Error(identified satisfies never);
    }

    const outcome = await otp.requestCode(auth.email);

    // A trusted device never sees the six boxes: the credential is already in
    // hand, so the page signs in from here and the code view never opens.
    if (outcome.outcome === "trusted") await completeLogin(outcome.customToken);
  }

  /** Spend the credential, however it was earned. Shared by the code path and
   * the trusted-device one so a failed sign-in reports itself once. */
  async function completeLogin(customToken: string) {
    const signedIn = await loginWithEmailOtp(customToken, null);
    if (signedIn.isSuccess) return;

    gaEventAuthFailed({
      code: resolveAuthErrorCode(signedIn.errorCode),
      method: "email",
      intent: "login",
      surface: "login_page",
      existing_provider: await lookupExistingProvider(auth.email),
    });
    displayToast(t("Something went wrong. Please try again."), "error");
  }

  async function handleSubmitCode(submitted: string) {
    const result = await otp.verifyCode(auth.email, submitted);

    // A transport failure, not a wrong code: the screen keeps the digits so the
    // user can retry without retyping them.
    if (result === null) {
      displayToast(t("Something went wrong. Please try again."), "error");
      return;
    }
    if (result.outcome === "rejected") {
      setCode("");
      return;
    }

    await completeLogin(result.customToken);
  }

  function handleResendCode() {
    void otp.resendCode(auth.email);
  }

  /**
   * Back to the address field. Without it a mistyped address is a dead end: no
   * code ever arrives, and Resend sends the next one to the same wrong mailbox.
   */
  function handleChangeEmail() {
    setCode("");
    otp.reset();
  }

  /**
   * Gestion de l'authentification OAuth (Apple/Google)
   */
  function handleOAuth(provider: "apple" | "google") {
    gaEventAuthMethodSelected({
      method: provider,
      intent: "login",
      surface: "login_page",
    });
    auth.handleOAuth(provider);
  }

  return {
    // État d'authentification
    email: auth.email,
    isOAuthLoading: auth.isOAuthLoading,

    // Actions de formulaire
    setEmail: auth.setEmail,

    // Actions d'orchestration
    handleOAuth,

    // Email OTP — the address is the whole credential.
    otpState: otp.state,
    otpCooldownRemaining: otp.cooldownRemaining,
    code,
    setCode,
    handleRequestCode,
    handleSubmitCode,
    handleResendCode,
    handleChangeEmail,
  };
}
